import React, { useEffect, useMemo, useState } from 'react';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminSupportingText,
  ADMIN_DETAIL_TABLE_CLASS,
} from '../shared/adminUx';
import {
  formatBillingAuditDateTime,
  getBillingAuditLogsForBusiness,
} from './businessBillingAuditLogData';

/** Date and Time | Event | Details | Related Record */
const AUDIT_LOG_GRID =
  'minmax(0, 1.1fr) minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 0.9fr)';

/**
 * Business Billing Record — Audit Log tab.
 */
const BusinessBillingAuditLogTab = ({ businessId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState(() => getBillingAuditLogsForBusiness(businessId));

  useEffect(() => {
    setRows(getBillingAuditLogsForBusiness(businessId));
    setCurrentPage(1);
  }, [businessId]);

  const itemsPerPage = CONFIG_LIST_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [rows.length, currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  const rangeStart = rows.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, rows.length);
  const showPagination = rows.length > itemsPerPage;

  return (
    <div className="min-w-0 max-w-full">
      <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} !mt-0 overflow-hidden`}>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <div
            className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
            style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
          >
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Date and Time</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Event</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Details</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Related Record</AdminTableHeader>
            </div>
          </div>

          {paginatedRows.length > 0 ? (
            paginatedRows.map((log, index) => (
              <div
                key={log.id || log.changeId || `billing-audit-${index}`}
                className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
              >
                <div className="flex items-start min-w-0">
                  <AdminTableMeta className="break-words">
                    {formatBillingAuditDateTime(log.timestamp || log.dateTime)}
                  </AdminTableMeta>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words">
                    {log.event || log.action || '—'}
                  </AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words whitespace-pre-wrap">
                    {log.details || '—'}
                  </AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words">
                    {log.relatedRecord || businessId || '—'}
                  </AdminTableBody>
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">
                No audit log entries
              </AdminSupportingText>
            </div>
          )}
        </div>
      </DashboardMainContainer>

      <ConfigurationListPaginationFooter
        show={rows.length > 0 && showPagination}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        totalCount={rows.length}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        goToPage={setCurrentPage}
      />
    </div>
  );
};

export default BusinessBillingAuditLogTab;
